import { useState, type FC } from 'react';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import BaseFormInputField from '@/base/Form/InputField';
import { type ApiResponse } from '@/api/tenantClient';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { groupRegistrationQueryKey, reassignRegistration, type RegistrationItem } from '@/api/registration/groupRegistration';
import {
  UiButton,
  UiDivider,
  UiHStack,
  UiModal,
  UiModalBody,
  UiModalCloseButton,
  UiModalContent,
  UiModalOverlay,
  UiStack,
  uiStyles,
  UiTag,
  UiText,
} from '@/lib/ui';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';

interface ReassignModalProps {
  isOpen: boolean
  onClose: () => void
  groupRegistrationId: number
  selectedRegistration: RegistrationItem | null
}

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email').required('Required')
});

const ReassignModal: FC<ReassignModalProps> = ({ isOpen, onClose, selectedRegistration, groupRegistrationId }) => {
  const { eventId } = useRegisterRoute();
  const { userSession } = useUserSession();
  const queryClient = useQueryClient();
  const { createTenantApiRequest } = useTenantApi();
  const [errors, setErrors] = useState<string[]>([]);
  const { mutate: reassignMutate, isLoading } = useMutation<ApiResponse<any>, Error, { email: string }>({
    mutationFn: async (values: { email: string }) => {
      const payload = {
        email: values.email,
        registrationId: selectedRegistration?.id ?? 0,
        groupRegistrationId
      };
      return await reassignRegistration(createTenantApiRequest)(payload);
    },
    onSuccess: async (result) => {
      if (result.errors) {
        setErrors(result.errors);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: [groupRegistrationQueryKey, { eventId }, { token: userSession?.authToken }] });
      setErrors([]);
    },
    onError: (error) => {
      setErrors([error.message ?? 'Failed to reassign email']);
    }
  });

  const handleReAssign = (values: { email: string }) => {
    reassignMutate(values);
  };
  const destroyOnClose = () => {
    onClose();
    setErrors([]);
  };
  return <UiModal isOpen={isOpen}
    onClose={destroyOnClose}
    size='md'
    motionPreset={'scale'}
    closeOnEsc
    closeOnOverlayClick
    isCentered>
    <UiModalOverlay />
    <Formik<{ email: string }>
      initialValues={{
        email: ''
      }}
      validationSchema={validationSchema}
      onSubmit={handleReAssign}>
      {
        ({ touched }) => {
          return <Form>
            <UiModalContent>
              <UiModalCloseButton />
              <UiModalBody px={{base: 4, lg: 8}} py={8}>
                <UiStack
                  p={4}
                  borderRadius={uiStyles.borderRadius}
                  bgColor={'blackAlpha.50'}
                  spacing={1}
                >
                  <UiText variant={'body2'} color={'text.secondary'}>Current attendee</UiText>
                  <UiText>{selectedRegistration?.name} ({selectedRegistration?.email})</UiText>
                </UiStack>
                {/*<BaseDividerHorizontal height={4} />*/}
                <UiStack
                  bgColor={'#fff'}
                  borderRadius={uiStyles.borderRadius}
                  pt={4}
                >
                  <UiHStack borderRadius={uiStyles.borderRadius}>
                    <BaseFormInputField
                      label='New attendee (email)'
                      name='email'
                      layout='stack'
                      placeholder='New email' />
                  </UiHStack>
                  <UiStack spacing={2} flexGrow={1} py={2}>
                    {errors.map((error) => {
                      return (
                        <BaseMessageBarError p={2} key={error}>
                          {error}
                        </BaseMessageBarError>
                      );
                    })}
                  </UiStack>
                  <UiStack display='flex' alignItems='flex-end'>
                    <UiButton type='submit' width='fit-content' isLoading={isLoading}>Confirm</UiButton>
                  </UiStack>
                </UiStack>
              </UiModalBody>
            </UiModalContent>
          </Form>;
        }}
    </Formik>
  </UiModal>;
};

export default ReassignModal;
